import {
  Button,
  ButtonRow,
  classNames,
  DateInput,
  Form,
  FormRow,
  InputError,
  LocaleProvider,
  Option,
  SegmentedButton,
  ValidInput,
} from '@op/opds.opux';
import { Stepper } from '@op/opds.opux-stepper';
import { formatISO, isEqual } from 'date-fns';
import { type ValidationErrors } from 'final-form';
import { type KeyboardEvent, useEffect, useState } from 'react';
import { Field, Form as FinalForm } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styles from './EditableForm.module.scss';
import {
  formLocalDateUTC,
  formatDate,
  formatEuros,
  getInitialState,
  getPath,
  getValidAgeRange,
  toBackgroundInfoForm,
} from '../../../utils';
import layoutStyles from '../../components/common/Layout.module.scss';
import PopupHintWrapper, {
  PopupParagraphs,
} from '../../components/PopupHintWrapper';
import { WorkingStatus, SmokingStatus } from '../../types/backgroundInfoForm';
import type { PurchasePathState } from '../../types/initialState';
import { TileHeading } from '../common/TileHeading';
import InfoSection from '../InfoSection';
import { AnalyticsPhases, trackAction } from 'api/analytics-api';
import i18n, { getCurrentLanguage } from 'i18n';
import { CustomTile } from 'individual/components/common/CustomTile';
import { useAppStore } from 'state/store';
import { toEditableForm } from 'state/util';
import { focusFirstError, isAgeOutOfRange, required } from 'utils/validators';

export interface EditableFormValues {
  dateOfBirth: Date | undefined;
  monthlyIncome: string;
  debt: string;
  children: number;
  smokingStatus: SmokingStatus | null;
  workingStatus: WorkingStatus | null;
}

export function validateDateOfBirth(
  date: Date | string | undefined | null,
  minValidAge: number,
  maxValidAge: number
): string | undefined {
  const dateToValidate = typeof date === 'string' ? getInputDate(date) : date;

  if (!dateToValidate) {
    return i18n.t('background.errors.dateError');
  }

  if (isAgeOutOfRange(dateToValidate, minValidAge, maxValidAge)) {
    return i18n.t('background.errors.ageValidationOutOfRange', {
      min: minValidAge,
      max: maxValidAge,
    });
  }
}

export function validateAmount(
  x: string,
  maxValue: number
): string | undefined {
  const asInt = parseInt(x);
  if (Number.isNaN(asInt) || Number(x).toString() !== x) {
    return i18n.t('background.errors.amountError');
  }
  if (asInt < 0) {
    return i18n.t('background.errors.negativeAmountError');
  }
  if (asInt > maxValue) {
    return i18n.t('background.errors.maxAmountError', {
      max: formatEuros(maxValue),
    });
  }
}

export function validateCalculatorForm(
  form: EditableFormValues,
  authenticated: boolean,
  backgroundConstraints: {
    maxMonthlyIncome: number;
    maxDebt: number;
    maxChildren: number;
  },
  validAgeRange: {
    min: number;
    max: number;
  }
): ValidationErrors {
  const errors = {
    dateOfBirth: authenticated
      ? undefined
      : validateDateOfBirth(
          form.dateOfBirth,
          validAgeRange.min,
          validAgeRange.max
        ),
    monthlyIncome: validateAmount(
      form.monthlyIncome,
      backgroundConstraints.maxMonthlyIncome
    ),
    workingStatus: required(form.workingStatus),
    debt: validateAmount(form.debt, backgroundConstraints.maxDebt),
    smokingStatus: required(form.smokingStatus),
  };
  if (Object.values(errors).filter((x) => x !== undefined).length > 0) {
    return errors;
  }
}

export const getInputDate = (inputValue?: string): Date | null => {
  const match = inputValue?.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (match) {
    const day = parseInt(match[1], 10);
    const month = parseInt(match[2], 10) - 1;
    const year = parseInt(match[3], 10);
    const date = new Date(year, month, day);
    if (
      date.getFullYear() === year &&
      date.getMonth() === month &&
      date.getDate() === day
    ) {
      return date;
    }
  }
  return null;
};

const trackChildrenClicked = () => {
  trackAction(
    'huollettavat-clicked',
    'button',
    AnalyticsPhases.CALCULATE_PRICE
  );
};

const DATE_OF_BIRTH_ID = 'dateOfBirth';
const DEBT_ID = 'debt';
const MONTHLY_INCOME_ID = 'monthlyIncome';

const EditableForm = () => {
  const navigate = useNavigate();

  const { t } = useTranslation(['individualLife']);

  const { marketableProduct, currentDate, authenticatedCustomer } =
    getInitialState() as PurchasePathState;
  const backgroundInfoForm = useAppStore.use.backgroundInfoForm();
  const setBackgroundInfoForm = useAppStore.use.setBackgroundInfoForm();
  const submitBackgroundInfo = useAppStore(
    (state) => state.submitBackgroundInfo
  );

  const [editableFormValues, setEditableFormValues] =
    useState<EditableFormValues>(toEditableForm(backgroundInfoForm));

  useEffect(() => {
    setEditableFormValues(toEditableForm(backgroundInfoForm));
  }, [backgroundInfoForm]);

  const today = new Date(currentDate);

  const validAgeRange = getValidAgeRange(marketableProduct);
  const minValidAge = validAgeRange.min;
  const maxValidAge = validAgeRange.max;

  const backgroundConstraints = {
    maxMonthlyIncome: marketableProduct.maxMonthlyIncome,
    maxDebt: marketableProduct.maxDebt,
    maxChildren: marketableProduct.maxChildren,
  };

  const { maxChildren } = backgroundConstraints;

  function validateForm(form: EditableFormValues): ValidationErrors {
    return validateCalculatorForm(
      form,
      authenticatedCustomer,
      backgroundConstraints,
      {
        min: minValidAge,
        max: maxValidAge,
      }
    );
  }

  async function onValidForm(formValues: EditableFormValues) {
    setEditableFormValues(formValues);
    setBackgroundInfoForm(toBackgroundInfoForm(formValues));
    await submitBackgroundInfo(formValues).then((value) => {
      navigate(getPath(t('paths.insuranceContent')));
      return value;
    });
  }

  async function onSubmit(form: EditableFormValues) {
    const errors = validateForm(form);
    if (errors) {
      return errors;
    }

    await onValidForm({
      ...form,
      dateOfBirth: authenticatedCustomer
        ? editableFormValues.dateOfBirth
        : form.dateOfBirth
          ? new Date(formatISO(form.dateOfBirth, { representation: 'date' }))
          : undefined,
    });
  }

  const handleFormKeyDown = (event: KeyboardEvent<HTMLFormElement>) => {
    if (
      event.key === 'Enter' &&
      event.target instanceof HTMLInputElement &&
      event.target.type !== 'submit'
    ) {
      event.preventDefault();
    }
  };

  const getInitialDate = () => {
    if (!editableFormValues.dateOfBirth) {
      return undefined;
    }
    const dateWithZeroTime = new Date(
      formLocalDateUTC(editableFormValues.dateOfBirth)
    );
    const timezoneOffset = dateWithZeroTime.getTimezoneOffset();
    if (timezoneOffset > 0) {
      // timezone before (-) compared to utc
      // move the time to the nearest next midnight
      dateWithZeroTime.setHours(24, 0, 0, 0);
    } else if (timezoneOffset < 0) {
      // timezone after (+) compared to utc
      // No need to move the tme but to be consistant move the time to the latest midnight
      dateWithZeroTime.setHours(0, 0, 0, 0);
    }
    return dateWithZeroTime;
  };

  const initialDate = getInitialDate();

  return (
    <FinalForm
      onSubmit={onSubmit}
      validate={validateForm}
      validateOnBlur={true}
      render={({ handleSubmit, errors }) => (
        <Form
          onSubmit={handleSubmit}
          onKeyDown={(e) => {
            handleFormKeyDown(e);
          }}
          // @ts-expect-error noValidate does exist on HTMLFormElement
          noValidate>
          <CustomTile>
            <TileHeading className={styles.tileHeading}>
              {i18n.t('background.form.infoBlockHeader')}
            </TileHeading>
            <FormRow
              className={classNames(
                styles.formRow,
                authenticatedCustomer
                  ? styles.authenticatedDateOfBirthLabel
                  : ''
              )}
              label={i18n.t('background.form.dateOfBirth')}
              labelFor={DATE_OF_BIRTH_ID}
              required>
              {authenticatedCustomer && editableFormValues.dateOfBirth && (
                <div>{formatDate(editableFormValues.dateOfBirth)}</div>
              )}
              <div className={authenticatedCustomer ? styles.hidden : ''}>
                <Field name={DATE_OF_BIRTH_ID} initialValue={initialDate}>
                  {({ input, meta }) => (
                    <LocaleProvider locale={getCurrentLanguage()}>
                      <DateInput
                        onClick={() => {
                          // only track the action on the first click
                          if (!meta.touched) {
                            trackAction(
                              'syntaika-clicked',
                              'button',
                              AnalyticsPhases.CALCULATE_PRICE
                            );
                          }
                        }}
                        allowPastDates
                        maxDate={today}
                        invalid={meta.touched && meta.invalid}
                        value={input.value}
                        onBlur={input.onBlur}
                        inputProps={{
                          title: meta.error,
                          id: DATE_OF_BIRTH_ID,
                          placeholder: i18n.t(
                            'background.form.dateOfBirthPlaceholder'
                          ),
                          'data-testid': 'birthday-input',
                          autoComplete: 'off',
                          'aria-required': true,
                        }}
                        onChange={(e) => {
                          const inputDate = getInputDate(e?.inputValue);
                          if (inputDate) {
                            // inputDate is defined
                            if (!isEqual(inputDate, input.value)) {
                              // Only trigger onChange if dates are not equal
                              input.onChange(inputDate);
                            }
                          } else {
                            // If date is not defined, keep the input value as it is
                            input.onChange(e?.inputValue);
                          }
                        }}
                        disabled={authenticatedCustomer}
                        customErrorMessage={
                          validateDateOfBirth(
                            input.value,
                            minValidAge,
                            maxValidAge
                          ) ?? meta.error
                        }
                        isOutsideRange={(date) =>
                          isAgeOutOfRange(date, minValidAge, maxValidAge)
                        }
                        className={styles.inputWrapper}
                      />
                    </LocaleProvider>
                  )}
                </Field>
              </div>
            </FormRow>
            <FormRow
              className={styles.formRow}
              label={i18n.t('background.form.monthlyIncome')}
              labelFor={MONTHLY_INCOME_ID}
              hint={
                <PopupHintWrapper
                  placement='top-end'
                  data-testid='monthly-income-popup'
                  analyticsData={{
                    actionName: 'tulot-info-clicked',
                    phase: AnalyticsPhases.CALCULATE_PRICE,
                  }}
                  ariaLabel={i18n.t('common.additionalInformationAriaLabel', {
                    context: i18n.t('background.form.monthlyIncome'),
                  })}>
                  {i18n.t('background.form.monthlyIncomeHint')}
                </PopupHintWrapper>
              }
              required>
              <Field
                name={MONTHLY_INCOME_ID}
                initialValue={editableFormValues.monthlyIncome}>
                {({ input, meta }) => (
                  <ValidInput
                    autoComplete='off'
                    amount
                    min={0}
                    minLength={1}
                    unit={i18n.t('common.eurosPerMonth')}
                    unitPos='outside'
                    error={meta.touched && meta.invalid && meta.error}
                    value={input.value}
                    onChange={input.onChange}
                    onBlur={input.onBlur}
                    title={meta.error}
                    wrapperProps={{ className: styles.inputWrapper }}
                    data-testid='monthly-income-input'
                    onClick={() => {
                      if (!meta.touched) {
                        trackAction(
                          'kk-tulot-clicked',
                          'button',
                          AnalyticsPhases.CALCULATE_PRICE
                        );
                      }
                    }}
                    id={MONTHLY_INCOME_ID}
                    aria-required={true}
                  />
                )}
              </Field>
            </FormRow>
            <FormRow
              label={i18n.t('background.form.workingStatusQuestion')}
              className={classNames(styles.noSelection, styles.formRow)}
              hint={
                <PopupHintWrapper
                  placement='top-end'
                  data-testid='working-popup'
                  analyticsData={{
                    actionName: 'tyoelamassa-info-clicked',
                    phase: AnalyticsPhases.CALCULATE_PRICE,
                  }}
                  ariaLabel={i18n.t('common.additionalInformationAriaLabel', {
                    context: i18n.t('background.form.workingStatusQuestion'),
                  })}>
                  <PopupParagraphs
                    texts={
                      t('background.form.workingStatusHint', {
                        returnObjects: true,
                      }) as string[] | undefined
                    }
                  />
                </PopupHintWrapper>
              }
              labelFor={'workingStatus'}
              required>
              <Field
                name='workingStatus'
                type='radio'
                value={editableFormValues.workingStatus}
                initialValue={editableFormValues.workingStatus}>
                {({ input, meta }) => {
                  const showError =
                    meta.invalid && !!meta.error && meta.submitFailed;
                  return (
                    <>
                      <SegmentedButton
                        value={input.value ?? undefined}
                        onChange={(e) => {
                          input.onChange(e);
                          // Validate the input
                          input.onBlur();
                        }}
                        invalid={showError}
                        aria-required='true'>
                        <Option
                          aria-describedby='workingStatus-error'
                          value={WorkingStatus.YES}
                          data-testid='working-yes-button'
                          onClick={() => {
                            trackAction(
                              'tyoelamassa-kylla-clicked',
                              'button',
                              AnalyticsPhases.CALCULATE_PRICE
                            );
                          }}>
                          {i18n.t('background.form.yes')}
                        </Option>
                        <Option
                          aria-describedby='workingStatus-error'
                          value={WorkingStatus.NO}
                          data-testid='working-no-button'
                          onClick={() => {
                            trackAction(
                              'tyoelamassa-ei-clicked',
                              'button',
                              AnalyticsPhases.CALCULATE_PRICE
                            );
                          }}>
                          {i18n.t('background.form.no')}
                        </Option>
                      </SegmentedButton>
                      <InputError
                        error={
                          showError ? new Error(i18n.t(meta.error)) : undefined
                        }
                        id={'workingStatus-error'}
                      />
                    </>
                  );
                }}
              </Field>
            </FormRow>
            <FormRow
              className={styles.formRow}
              label={i18n.t('background.form.debt')}
              labelFor={DEBT_ID}
              hint={
                <PopupHintWrapper
                  placement='top-end'
                  data-testid='debt-popup'
                  analyticsData={{
                    actionName: 'velat-info-clicked',
                    phase: AnalyticsPhases.CALCULATE_PRICE,
                  }}
                  ariaLabel={i18n.t('common.additionalInformationAriaLabel', {
                    context: i18n.t('background.form.debt'),
                  })}>
                  {i18n.t('background.form.debtHint')}
                </PopupHintWrapper>
              }
              required>
              <Field name={DEBT_ID} initialValue={editableFormValues.debt}>
                {({ input, meta }) => (
                  <ValidInput
                    autoComplete='off'
                    amount
                    min={0}
                    minLength={1}
                    unit={i18n.t('background.form.debtUnit')}
                    unitPos='outside'
                    error={meta.touched && meta.invalid && meta.error}
                    value={input.value}
                    onChange={input.onChange}
                    onBlur={input.onBlur}
                    title={meta.error}
                    wrapperProps={{ className: styles.inputWrapper }}
                    data-testid='debt-input'
                    onClick={() => {
                      if (!meta.touched) {
                        trackAction(
                          'velat-clicked',
                          'button',
                          AnalyticsPhases.CALCULATE_PRICE
                        );
                      }
                    }}
                    id={DEBT_ID}
                    aria-required={true}
                  />
                )}
              </Field>
            </FormRow>
            <FormRow
              className={styles.formRow}
              label={i18n.t('background.form.children')}
              labelFor='children-input'
              required>
              <Field name='children' initialValue={editableFormValues.children}>
                {({ input }) => (
                  <div className={styles.stepperStyles}>
                    <Stepper
                      inputProps={{
                        'data-testid': 'children-input',
                        onClick: () => {
                          trackChildrenClicked();
                        },
                      }}
                      required
                      strict
                      value={input.value}
                      onChange={(value) => {
                        input.onChange(value);
                        trackChildrenClicked();
                      }}
                      onBlur={input.onBlur}
                      max={maxChildren}
                      decreaseButtonProps={{
                        'data-testid': 'decrease-button-children',
                      }}
                      increaseButtonProps={{
                        'data-testid': 'increase-button-children',
                      }}
                    />
                  </div>
                )}
              </Field>
            </FormRow>
            <FormRow
              label={i18n.t('background.form.smokingStatus')}
              className={classNames(styles.noSelection, styles.formRow)}
              hint={
                <PopupHintWrapper
                  placement='top-end'
                  data-testid='smoking-popup'
                  analyticsData={{
                    actionName: 'tupakointi-info-clicked',
                    phase: AnalyticsPhases.CALCULATE_PRICE,
                  }}
                  ariaLabel={i18n.t('common.additionalInformationAriaLabel', {
                    context: i18n.t('background.form.smokingStatus'),
                  })}>
                  {i18n.t('background.form.smokingStatusHint')}
                </PopupHintWrapper>
              }
              labelFor={'smokingStatus'}
              required>
              <Field
                name='smokingStatus'
                type='radio'
                value={editableFormValues.smokingStatus}
                initialValue={editableFormValues.smokingStatus}>
                {({ input, meta }) => {
                  const showError =
                    meta.invalid && !!meta.error && meta.submitFailed;
                  return (
                    <>
                      <SegmentedButton
                        value={input.value ?? undefined}
                        onChange={(e) => {
                          input.onChange(e);
                          // Validate the input
                          input.onBlur();
                        }}
                        invalid={showError}
                        aria-required='true'>
                        <Option
                          aria-describedby='smokingStatus-error'
                          value={SmokingStatus.YES}
                          data-testid='smoking-yes-button'>
                          {i18n.t('background.form.yes')}
                        </Option>
                        <Option
                          aria-describedby='smokingStatus-error'
                          value={SmokingStatus.NO}
                          data-testid='smoking-no-button'>
                          {i18n.t('background.form.no')}
                        </Option>
                      </SegmentedButton>
                      <InputError
                        error={
                          showError ? new Error(i18n.t(meta.error)) : undefined
                        }
                        id={'smokingStatus-error'}
                      />
                    </>
                  );
                }}
              </Field>
            </FormRow>
          </CustomTile>
          <div className={layoutStyles.columnReverse}>
            <InfoSection />
            <ButtonRow id='buttonRow' stacked>
              <Button
                onClick={() => {
                  focusFirstError(errors);
                }}
                primary
                type='submit'
                className={styles.noSelection}
                data-testid='continue-button'>
                {i18n.t('background.form.continue')}
              </Button>
            </ButtonRow>
          </div>
        </Form>
      )}
    />
  );
};

export default EditableForm;
